import type { LiveDrone } from '@/hooks/useWebSocket';
import type { ManualControlHold } from '@/types/coordination';
import type { MapMode } from '@/lib/map-mode';
import { getApiUrl } from '@/lib/api';
import type { HandoverLink } from './handoverLink';

/**
 * Why an aircraft is being selected.
 *
 * Only `'map-pick'` is the operator reaching for the aircraft itself — a click
 * on its marker on the tactical picture. Everything else that selects does so
 * on the way to something else, and the COP treats the two differently: see
 * `DroneMapProps.onSelectDrone`.
 */
export type DroneSelectSource = 'map-pick';

// CARTO's 'dark-matter' style path (and all its raster tile paths) time out on
// some restrictive networks - confirmed on the AirScort office network: the
// dark-matter style.json and every CARTO raster/dark path hang for 75s, while
// only the 'voyager' GL vector style resolves. So 'dark' now uses OpenFreeMap's
// self-hosted dark vector style (free, no API key, different CDN that IS
// reachable there - style + tiles both verified <1s). 'bright'/light keeps
// CARTO voyager (also reachable). A genuine dark basemap, not a CSS-dimmed
// light one - so no filter hack, and status-marker colors stay accurate.
// Satellite is served by OUR api, not a CDN. The style document is generated per
// request because it carries a Google session token, and every tile is proxied so
// the billable API key never reaches a browser (src/api/basemap.ts). Resolving the
// base at module scope is safe: in the browser this file is evaluated after
// `window` exists, and on a non-dev host getApiUrl() returns '' - a relative URL
// MapLibre resolves against the page origin, which is what nginx serves.
//
// Unlike the two CDN styles this one can be ABSENT: a deployment with no
// GOOGLE_MAPS_API_KEY answers 501. Whether to offer it - and, since it became the
// default basemap, whether to open on it - is decided by the `satellite_basemap`
// switch on /api/auth/me, never by a probe from here: the map mounts before login
// completes, so a probe on mount reads no token and, with an empty dependency
// array, never asks again. A synchronous default cannot await that answer either,
// which is why `droneMapReducer` caches the last one it saw
// (SATELLITE_AVAILABLE_STORAGE_KEY) and defaults to the dark map when it has
// never seen one.
export const SATELLITE_STYLE_URL = `${getApiUrl()}/api/basemap/satellite/style.json`;

export const STYLES = {
  dark: 'https://tiles.openfreemap.org/styles/dark',
  bright: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
  satellite: SATELLITE_STYLE_URL,
} as const;

/**
 * The bearer-attaching `transformRequest` every MapLibre map must be built with.
 *
 * SHARED because the basemap CHOICE is shared. `getPersistedMapStyle` is read by
 * six maps - the COP, both editors, the mission route, the sites overview and
 * the flight replay - and `satellite` is now a value it can return. A map
 * constructed without this closure asks our API for a style with no
 * Authorization header, gets 401, and never fires `load`. In four of those six
 * the content is added inside the `load` handler, so it is not only the ground
 * that goes missing: the mission route, the site markers and the flight track
 * are never drawn at all, and localStorage makes it survive a reload.
 *
 * One helper rather than six copies, so a seventh map cannot be added wrong.
 */
export function basemapTransformRequest(url: string): { url: string; headers?: Record<string, string> } {
  if (!isOwnApiStyleUrl(url)) return { url };
  let token: string | null = null;
  try {
    token = localStorage.getItem('access_token');
  } catch {
    // Privacy mode or a locked-down policy. Unauthenticated is the honest
    // outcome; the basemap fails and the caller's fallback handles it.
  }
  return token ? { url, headers: { Authorization: `Bearer ${token}` } } : { url };
}

/**
 * Requests our own API serves, which need the operator's bearer attached.
 *
 * Compares the RESOLVED origin and path, not a string prefix. The first cut was
 * `url.startsWith(getApiUrl() + '/api/basemap/')`, and on a deployed host
 * `getApiUrl()` is '' - so it matched the relative style URL and missed every
 * tile, because the style document hands MapLibre ABSOLUTE tile URLs (relative
 * ones resolve against the page origin, which is a different port from the API
 * in dev). The symptom was precise and misleading: the style loaded, the
 * attribution rendered, and every tile came back 401 onto a black map.
 *
 * The origin check is the security half - a bare pathname test would attach the
 * operator's token to `https://anywhere.example/api/basemap/x` if a style ever
 * referenced one.
 */
export function isOwnApiStyleUrl(url: string): boolean {
  if (typeof window === 'undefined') return false;
  let target: URL;
  let allowed: URL;
  try {
    target = new URL(url, window.location.href);
    // getApiUrl() is '' for same-origin deployments; window.location is then ours.
    allowed = new URL(getApiUrl() || window.location.origin, window.location.href);
  } catch {
    return false;
  }
  return target.origin === allowed.origin && target.pathname.startsWith('/api/basemap/');
}

/** The NIGHT ground, used only when the page's --background cannot be read. */
export const OFFLINE_FALLBACK_GROUND = '#101419';

/**
 * Zero-network fallback style (no `sources`, so nothing to fetch). Used when
 * the external CDN style fails to load - e.g. blocked by an ad-blocker or a
 * restrictive network - so the map still renders drones/docks on a plain
 * background instead of staying blank forever. The background is the mode's
 * page ground (T1, D3), so the fallback is not a black hole under DAY.
 */
export function offlineFallbackStyle(background: string) {
  return {
    version: 8,
    sources: {},
    layers: [{ id: 'background', type: 'background', paint: { 'background-color': background } }],
  } as const;
}

export const STATUS_COLORS: Record<string, string> = {
  flying: '#22C55E',
  active: '#22C55E',
  idle: '#3B82F6',
  warning: '#F59E0B',
  critical: '#EF4444',
  offline: '#6B7280',
};

export const EMPTY_COOPERATIVE: CooperativeTraffic[] = [];
export const EMPTY_GEOFENCES: GeofenceOverlay[] = [];
export const EMPTY_CONTROLLERS: RcController[] = [];
export const EMPTY_SITES: SiteMarker[] = [];
export const EMPTY_ZONE_DRAW_POINTS: [number, number][] = [];
export const EMPTY_MISSIONS: DroneMapMission[] = [];
export const EMPTY_PLAN_WAYPOINTS: PlanRoutePoint[] = [];
/** Stable empty default, so a map rendered without holds keeps one identity. */
export const EMPTY_MANUAL_HOLDS: ReadonlyMap<string, ManualControlHold> = new Map();
export const EMPTY_TRIGGERS: TriggerMarker[] = [];

export interface CooperativeTraffic {
  id: string;
  callsign: string;
  latitude: number;
  longitude: number;
  altitude: number;
  heading: number;
  speed?: number;
  last_seen?: string;
  type: 'rid' | 'adsb' | 'unknown';
}

export interface GeofenceOverlay {
  id: string;
  name: string;
  site_id?: string | null;
  type: 'operational' | 'restricted' | 'warning' | 'jamming';
  geometry: GeoJSON.Geometry;
  color: string;
  /** Mirrors geofences.is_active - only active keep-in fences gate mission plans. */
  is_active?: boolean;
}

export interface RcController {
  drone_id: string;
  latitude: number;
  longitude: number;
}

export interface SiteMarker {
  id: string;
  name: string;
  address?: string | null;
  latitude: number;
  longitude: number;
  radius_meters: number;
  is_active?: boolean;
}

export interface DroneMapMission {
  id: string;
  name: string;
  status: string;
  drone_callsign?: string | null;
  /** Which airframe holds this mission. Used to suppress the transit advisory
   *  line when the selected aircraft is the one already flying this route. */
  assigned_drone_id?: string | null;
  /**
   * 022/#700. An ORBIT stores exactly ONE waypoint, so `buildMissionFeatures` drew it as a lone
   * dot and the circle the aircraft is actually flying appeared nowhere outside compose. These
   * are what let the ring be drawn for a saved or in-progress orbit, using the same
   * `circleRingCoords` as the range rings and the circle geofence - so a 500 m orbit and a 500 m
   * zone can never disagree about where their edge is.
   */
  type?: string | null;
  orbit_radius_m?: number | null;
  orbit_direction?: 'cw' | 'ccw' | null;
  waypoints: { latitude: number; longitude: number; altitude: number; sequence_order: number }[];
}

export interface DockMarker {
  id: string;
  callsign: string | null;
  latitude: number;
  longitude: number;
  status: string;
  is_online: boolean;
  /** Where a failed tag-guided landing falls back to; drawn so the operator
   *  SEES where the aircraft will come down. Absent = none configured. */
  safe_land?: { latitude: number; longitude: number } | null;
}

/**
 * An unanswered external trigger, drawn as a point the operator can dispatch to.
 *
 * `latitude`/`longitude` are NOT nullable here, unlike the row they come from:
 * a trigger with no reported fix has nothing to draw, so the COP filters it out
 * before it reaches the map rather than making every layer handle a null.
 */
export interface TriggerMarker {
  id: string;
  latitude: number;
  longitude: number;
  /** 0-100 as the source reported it. Null when it reports no confidence. */
  score: number | null;
  occurred_at: string;

  /**
   * The aircraft the PLATFORM chose, when automatic dispatch is armed (#253).
   *
   * Null on every trigger under the default policy, which is off - so a deployment that has not
   * armed anything renders exactly the card it always did.
   *
   * REQUIRED, NOT OPTIONAL, and every field below it with them (review, 2026-09-12). They were
   * optional, and `triggerMarkers` in `(dashboard)/page.tsx` simply left `dispatch_refusal` out
   * - so the card's whole "Nothing was sent" panel was unreachable in the running app, silently,
   * with a component test passing against a prop the one real caller never populated. A missing
   * field has to be a type error here, because the failure it produces is invisible: the operator
   * sees an ordinary red point and an armed policy that refuses everything looks exactly like one
   * that is switched off.
   */
  proposed_drone_id: string | null;
  proposed_altitude_m: number | null;
  /** How the proposal was reached. `full_auto` means it already flew; there is nothing to approve. */
  proposal_mode: 'propose' | 'full_auto' | null;
  /** When an unanswered proposal goes void and the detection returns to the board. */
  proposal_expires_at: string | null;
  /** Every aircraft weighed and why it was passed over - including when NONE was chosen. */
  proposal_considered: TriggerConsidered[] | null;
  /**
   * Why the platform's last attempt produced no sortie.
   *
   * The only account an operator gets under full_auto, where nobody watched the attempt happen.
   * Without it, an armed policy that refuses every detection is indistinguishable from one that
   * is switched off.
   */
  dispatch_refusal: string | null;
}

/** One aircraft the selector weighed, and what it concluded. */
export interface TriggerConsidered {
  droneId: string;
  name: string;
  callsign: string | null;
  /** Null on the chosen aircraft, and on the ones behind it that were simply not needed. */
  rejection: string | null;
  detail: string | null;
}

/** Draft mission waypoint rendered by the map planner overlay. */
export interface PlanRoutePoint {
  lat: number;
  lng: number;
  /** True when the waypoint has a blocking validation error (rendered red). */
  invalid: boolean;
}

/**
 * The VIDEO key's state and verb: "video beside the map" for the selected
 * aircraft. Owned by the COP (the stage is the COP's, not the map's); the map
 * only draws the key and reports the press.
 */
export interface VideoBesideControl {
  on: boolean;
  onToggle: () => void;
}

export interface DroneMapProps {
  drones: LiveDrone[];
  selectedDroneId: string | null;
  /**
   * Who is hand-flying what, org-wide — `useWebSocket`'s `manualControlMap`,
   * passed through untouched.
   *
   * Marks the held aircraft on the picture itself (amber ring + MANUAL tag), so
   * a second operator sees the hand on the sticks BEFORE they select the track
   * and press something. Freshness is decided per frame inside the source
   * builder; see `UpdateSourcesParams.manualHolds`.
   */
  manualHolds?: ReadonlyMap<string, ManualControlHold>;
  /**
   * Select this aircraft. `''` clears the selection (the background click).
   *
   * `source` says WHY, because not every selection means the same thing to the
   * COP. `'map-pick'` is the operator putting a finger on the aircraft itself
   * and asking to see it; that is the one the COP answers by opening the
   * aircraft's camera over the map. Every other route here — the range-rings
   * menu item that has to select before it can draw, the camera menu item that
   * opens its own window, the strip board in the left column — is selecting as
   * a means to something else, and must not be read as a request for video.
   *
   * Omitted rather than defaulted, so a caller that has not thought about it
   * gets the conservative answer.
   */
  onSelectDrone: (id: string, source?: DroneSelectSource) => void;
  /**
   * Content anchored to the selected drone's marker — the selection chip.
   *
   * A plain node, because the map no longer has anything to hand it. This was a
   * render function while DroneStateCard carried FOLLOW and TRACK buttons that
   * had to read the map's own follow/trail state; those toggles now live in
   * DroneMapControls, where the map owns both ends of the wire directly.
   */
  selectedDroneOverlay?: React.ReactNode;
  docks?: DockMarker[];
  selectedDockId?: string | null;
  onSelectDock?: (id: string) => void;
  /** Unanswered external triggers, each with a reported position. */
  triggers?: TriggerMarker[];
  /** Send an aircraft to this trigger's point. Absent = the operator may not dispatch. */
  onDispatchFromTrigger?: (trigger: TriggerMarker) => void;
  /** Clear the trigger without sending anything. */
  onDismissTrigger?: (id: string) => void;
  /** Approve the aircraft the platform proposed, and launch it (#253). */
  /**
   * Approve the platform's proposal and launch.
   *
   * RETURNS A PROMISE THAT SETTLES WHEN THE LAUNCH DOES, and the card holds itself open on it.
   * The launch runs the whole bay sequence server-side - up to `BAY_MOVE_TIMEOUT_HTTP_MS`, about
   * fifty seconds - and the card used to close on the click, so an approving operator got a
   * vanished card, a vanished point and nothing else for the best part of a minute: visually
   * identical to a Dismiss, which is how a launch gets authorised twice.
   */
  onApproveTrigger?: (id: string) => void | Promise<void>;
  /**
   * Refuse the proposed aircraft while LEAVING the detection open.
   *
   * Separate from dismiss on purpose: the person is still there, and an operator who does not
   * like the aircraft the platform picked should not have to clear the point to say so.
   */
  onRejectTrigger?: (id: string) => void;
  cooperativeTraffic?: CooperativeTraffic[];
  geofences?: GeofenceOverlay[];
  controllers?: RcController[];
  sites?: SiteMarker[];
  /**
   * What a click on this map MEANS right now — one value, never a set of flags.
   *
   * Every armed interaction (fly-here, dispatch pick, zone draw, dock plan,
   * mission compose/edit) is a variant of this one mode, so two of them cannot
   * be armed at once and no handler has to re-derive the arbitration. The
   * callbacks below are what each mode calls when its click lands; the mode
   * decides which of them is reachable. See `_cop/mapMode.ts`.
   */
  mapMode?: MapMode;
  onDispatchTargetPicked?: (latitude: number, longitude: number) => void;
  onRequestDispatchHere?: (latitude: number, longitude: number) => void;
  onGotoPointPicked?: (latitude: number, longitude: number) => void;
  onOpenVideo?: (droneId: string) => void;
  /**
   * "Video beside the map" for the selected aircraft (#505 note 4): the VIDEO key
   * in the control column, beside FOLLOW and TRACK. Absent or null, no key is
   * drawn at all - that is how a deployment without the switch, and a manual
   * session (whose banner owns the stage layout), cost nothing here. The stage
   * itself is the COP's; the map only reports the press.
   */
  videoBeside?: VideoBesideControl | null;
  /** Opens the COP's zones sheet, from the map's Layers menu. */
  onOpenZones?: () => void;
  zoneDrawPoints?: [number, number][];
  zonePolygonClosed?: boolean;
  onZoneMapClick?: (lat: number, lng: number) => void;
  zoneCircleCenter?: { lat: number; lng: number } | null;
  zoneCircleRadius?: number;
  /** Resolved colour of the zone being drawn, so the preview shows the
   *  operator's pick before they commit to it. Defaults to the palette's
   *  fallback when the caller draws without a picker. */
  zoneDrawColor?: string;
  missions?: DroneMapMission[];
  /**
   * Mission the operator has picked in the missions panel (or on the map).
   * Its route renders saturated and widest; every other mission route dims so
   * the picked one reads instantly. Null = no pick, all routes at rest salience.
   */
  selectedMissionId?: string | null;
  /** Mission under the cursor in the missions panel — brightened, not promoted. */
  hoveredMissionId?: string | null;
  /**
   * Continuous-coverage handovers in progress (#251), drawn as a dashed line from each relief to
   * the aircraft it is relieving, labelled with the time left to the handover. A line is drawn
   * only while both aircraft are on the picture with a live link and the outgoing is airborne.
   */
  handoverLinks?: HandoverLink[];
  /** Clicking a mission route line or one of its waypoints picks that mission. */
  onSelectMission?: (missionId: string) => void;
  /** Draft dock-mission route (map planner). Rendered whenever non-empty. */
  planWaypoints?: PlanRoutePoint[];
  /**
   * 022. Radius in metres of the circle an ORBIT will fly around its single placed point.
   * Omitted for every other mission type, and while the radius box holds nothing usable —
   * a ring at a guessed radius would misstate where the aircraft is going.
   */
  planOrbitRadiusM?: number | null;
  /** Launch point (dock location) the plan polyline starts from. */
  planHome?: { lat: number; lng: number } | null;
  /** Keeps existing waypoint markers draggable after point-entry is stopped.
   *  Independent of the armed mode: a locked route stays editable by drag. */
  planEditable?: boolean;
  onPlanModeExit?: () => void;
  planSelectedIndex?: number | null;
  /**
   * Zero-based index of the operator waypoint the assigned drone is currently
   * flying to, derived from telemetry mission_current. Null when no mission is
   * executing. Highlights the active leg and marks reached vs pending waypoints.
   */
  planActiveIndex?: number | null;
  onPlanMapClick?: (lat: number, lng: number) => void;
  onPlanWaypointClick?: (index: number) => void;
  onPlanWaypointDrag?: (index: number, lat: number, lng: number) => void;
  /**
   * Drop the map's own chrome — navigation keys, legend, airspace panel.
   *
   * For the manual-flight control minimap, which is 280 px square: at that size
   * the control column and the legend are most of the box, and none of them are
   * usable there anyway (the minimap is locked on the aircraft by design, so a
   * "reset north" or a style toggle is a control with nothing to act on). The
   * map, the aircraft and the flight surface are what is left.
   */
  compact?: boolean;
  /**
   * Chrome floating over the map, per edge, in CSS px.
   *
   * Under the COP's tactical shell the map is full bleed and the bars float on
   * top of it, so the canvas is larger than the map anyone can see. Two things
   * inside this component have to know: the selected aircraft's anchored card,
   * which would otherwise be clamped to the canvas and land under the command
   * bar, and the map's own bottom-left chrome, which would otherwise be covered
   * by it.
   *
   * All zero, the default, is the classic shell and the control minimap.
   */
  chromeInsets?: { top: number; right: number; bottom: number; left: number };
}

export interface ContextMenu {
  x: number;
  y: number;
  droneId: string;
  callsign: string;
}

export interface MapDispatchMenu {
  x: number;
  y: number;
  lat: number;
  lng: number;
}

/**
 * An external trigger's open card.
 *
 * Holds the trigger's ID, NOT a snapshot of the row. The card is resolved
 * against the live `triggers` list on every render, so when another operator
 * answers this detection - the exact race `trigger_resolved` exists for - the
 * card closes instead of continuing to offer "Dispatch drone here" for a
 * trigger that is already being flown to.
 *
 * No screen coordinates either: the card is projected from the trigger's own
 * lng/lat and re-anchored on map move, like the selected-drone overlay. A
 * stored x/y would leave the card nailed to a stale pixel as soon as the
 * operator panned.
 */
export interface TriggerCard {
  triggerId: string;
  /**
   * Wall clock when the card was opened, stamped in the click handler.
   *
   * The card shows the detection's age, and reading the clock during render is
   * an impure call React's lint rules refuse - correctly, since it makes the
   * rendered age depend on why the component happened to re-render. Freezing it
   * at open is also the better reading: a number that ticks under the operator
   * while they decide is noise, not information.
   */
  openedAt: number;
}

export type MapStyleKey = keyof typeof STYLES;
